/**
 * View State Management
 *
 * Pure functions for managing canvas editor view state.
 * All functions are immutable - they return new state objects.
 */

import type {
  ViewState,
  SelectionMode,
  Point,
  SelectionRect,
  EditorNode,
  EditorEdge,
  ResizeHandlePosition,
} from "./types";
import { worldToLocal, localToWorld, getLocalAnchor } from "./geometry";

// =============================================================================
// Initial State
// =============================================================================

/**
 * Create initial view state
 */
export function createInitialState(initialSelection?: string[]): ViewState {
  return {
    selection: new Set(initialSelection ?? []),
    selectionAnchor: null,
    boxSelect: null,
    drag: null,
    connecting: null,
    editing: null,
    hovered: {
      nodeId: null,
      edgeId: null,
      handleId: null,
      resizeHandleId: null,
      rotationZone: false,
    },
    resize: null,
    rotate: null,
    dropTarget: false,
  };
}

// =============================================================================
// Selection Operations
// =============================================================================

/**
 * Apply a selection operation to the current state.
 *
 * @param state - Current view state
 * @param nodeId - Node ID to select/deselect
 * @param mode - How to apply the selection
 * @returns New view state with updated selection
 */
export function applySelection(
  state: ViewState,
  nodeId: string,
  mode: SelectionMode,
): ViewState {
  const newSelection = new Set(state.selection);

  switch (mode) {
    case "replace":
      newSelection.clear();
      newSelection.add(nodeId);
      break;
    case "add":
      newSelection.add(nodeId);
      break;
    case "remove":
      newSelection.delete(nodeId);
      break;
    case "toggle":
      if (newSelection.has(nodeId)) {
        newSelection.delete(nodeId);
      } else {
        newSelection.add(nodeId);
      }
      break;
  }

  return {
    ...state,
    selection: newSelection,
    selectionAnchor: nodeId,
  };
}

/**
 * Select multiple nodes at once.
 *
 * @param state - Current view state
 * @param nodeIds - Node IDs to select
 * @param mode - How to apply the selection
 * @returns New view state with updated selection
 */
export function applyMultiSelection(
  state: ViewState,
  nodeIds: string[],
  mode: SelectionMode,
): ViewState {
  const newSelection = new Set(state.selection);

  switch (mode) {
    case "replace":
      newSelection.clear();
      for (const id of nodeIds) {
        newSelection.add(id);
      }
      break;
    case "add":
      for (const id of nodeIds) {
        newSelection.add(id);
      }
      break;
    case "remove":
      for (const id of nodeIds) {
        newSelection.delete(id);
      }
      break;
    case "toggle":
      for (const id of nodeIds) {
        if (newSelection.has(id)) {
          newSelection.delete(id);
        } else {
          newSelection.add(id);
        }
      }
      break;
  }

  return {
    ...state,
    selection: newSelection,
    selectionAnchor:
      nodeIds.length > 0 ? nodeIds[nodeIds.length - 1] : state.selectionAnchor,
  };
}

/**
 * Clear the current selection.
 */
export function clearSelection(state: ViewState): ViewState {
  if (state.selection.size === 0) {
    return state; // No change needed
  }

  return {
    ...state,
    selection: new Set(),
    selectionAnchor: null,
  };
}

/**
 * Select all nodes.
 */
export function selectAll(state: ViewState, allNodeIds: string[]): ViewState {
  return {
    ...state,
    selection: new Set(allNodeIds),
    selectionAnchor: allNodeIds.length > 0 ? allNodeIds[0] : null,
  };
}

/**
 * Check if a node is selected.
 */
export function isSelected(state: ViewState, nodeId: string): boolean {
  return state.selection.has(nodeId);
}

/**
 * Get array of selected node IDs.
 */
export function getSelectedIds(state: ViewState): string[] {
  return [...state.selection];
}

// =============================================================================
// Box Selection Operations
// =============================================================================

/**
 * Start a box selection.
 */
export function startBoxSelect(state: ViewState, origin: Point): ViewState {
  return {
    ...state,
    boxSelect: {
      origin,
      current: origin,
    },
  };
}

/**
 * Update the box selection as the user drags.
 */
export function updateBoxSelect(state: ViewState, current: Point): ViewState {
  if (!state.boxSelect) {
    return state;
  }

  return {
    ...state,
    boxSelect: {
      ...state.boxSelect,
      current,
    },
  };
}

/**
 * Get the current box selection rectangle (normalized to positive width/height).
 */
export function getBoxSelectRect(state: ViewState): SelectionRect | null {
  if (!state.boxSelect) {
    return null;
  }

  const { origin, current } = state.boxSelect;

  const x = Math.min(origin.x, current.x);
  const y = Math.min(origin.y, current.y);
  const width = Math.abs(current.x - origin.x);
  const height = Math.abs(current.y - origin.y);

  return { x, y, width, height };
}

/**
 * Finish box selection and select nodes within the box.
 *
 * @param state - Current view state
 * @param nodesInBox - Node IDs that intersect the box selection
 * @param mode - How to apply the selection (default: replace)
 */
export function finishBoxSelect(
  state: ViewState,
  nodesInBox: string[],
  mode: SelectionMode = "replace",
): ViewState {
  const stateWithSelection = applyMultiSelection(state, nodesInBox, mode);

  return {
    ...stateWithSelection,
    boxSelect: null,
  };
}

/**
 * Cancel box selection without changing selection.
 */
export function cancelBoxSelect(state: ViewState): ViewState {
  if (!state.boxSelect) {
    return state;
  }

  return {
    ...state,
    boxSelect: null,
  };
}

// =============================================================================
// Drag Operations
// =============================================================================

/**
 * Start dragging nodes.
 *
 * @param state - Current view state
 * @param nodes - Node IDs being dragged
 * @param origin - Starting point of drag
 * @param copying - Whether this is a copy operation (Alt+drag)
 */
export function startDrag(
  state: ViewState,
  nodes: string[],
  origin: Point,
  copying: boolean = false,
): ViewState {
  return {
    ...state,
    drag: {
      nodes,
      origin,
      current: origin,
      copying,
    },
  };
}

/**
 * Update drag position.
 */
export function updateDrag(state: ViewState, current: Point): ViewState {
  if (!state.drag) {
    return state;
  }

  return {
    ...state,
    drag: {
      ...state.drag,
      current,
    },
  };
}

/**
 * Get the current drag offset.
 */
export function getDragOffset(state: ViewState): Point | null {
  if (!state.drag) {
    return null;
  }

  return {
    x: state.drag.current.x - state.drag.origin.x,
    y: state.drag.current.y - state.drag.origin.y,
  };
}

/**
 * Finish drag operation.
 */
export function finishDrag(state: ViewState): ViewState {
  return {
    ...state,
    drag: null,
  };
}

/**
 * Cancel drag operation (positions not committed).
 */
export function cancelDrag(state: ViewState): ViewState {
  return {
    ...state,
    drag: null,
  };
}

/**
 * Check if currently dragging.
 */
export function isDragging(state: ViewState): boolean {
  return state.drag !== null;
}

// =============================================================================
// Hover Operations
// =============================================================================

/**
 * Set hover state for a node.
 */
export function setHoveredNode(
  state: ViewState,
  nodeId: string | null,
): ViewState {
  if (state.hovered.nodeId === nodeId) {
    return state;
  }

  return {
    ...state,
    hovered: {
      ...state.hovered,
      nodeId,
    },
  };
}

/**
 * Set hover state for an edge.
 */
export function setHoveredEdge(
  state: ViewState,
  edgeId: string | null,
): ViewState {
  if (state.hovered.edgeId === edgeId) {
    return state;
  }

  return {
    ...state,
    hovered: {
      ...state.hovered,
      edgeId,
    },
  };
}

/**
 * Set hover state for a handle.
 */
export function setHoveredHandle(
  state: ViewState,
  handleId: string | null,
): ViewState {
  if (state.hovered.handleId === handleId) {
    return state;
  }

  return {
    ...state,
    hovered: {
      ...state.hovered,
      handleId,
    },
  };
}

/**
 * Clear all hover states.
 */
export function clearHover(state: ViewState): ViewState {
  if (
    state.hovered.nodeId === null &&
    state.hovered.edgeId === null &&
    state.hovered.handleId === null &&
    state.hovered.resizeHandleId === null &&
    state.hovered.rotationZone === false
  ) {
    return state;
  }

  return {
    ...state,
    hovered: {
      nodeId: null,
      edgeId: null,
      handleId: null,
      resizeHandleId: null,
      rotationZone: false,
    },
  };
}

// =============================================================================
// Editing Operations
// =============================================================================

/**
 * Start inline editing.
 */
export function startEditing(
  state: ViewState,
  nodeId: string,
  field: string,
): ViewState {
  return {
    ...state,
    editing: {
      nodeId,
      field,
    },
  };
}

/**
 * Stop inline editing.
 */
export function stopEditing(state: ViewState): ViewState {
  if (!state.editing) {
    return state;
  }

  return {
    ...state,
    editing: null,
  };
}

/**
 * Check if currently editing.
 */
export function isEditing(state: ViewState): boolean {
  return state.editing !== null;
}

// =============================================================================
// Connection Operations
// =============================================================================

/**
 * Start an edge connection from a handle.
 */
export function startConnecting(
  state: ViewState,
  sourceNode: string,
  sourceHandle: string,
  currentPoint: Point,
): ViewState {
  return {
    ...state,
    connecting: {
      sourceNode,
      sourceHandle,
      currentPoint,
    },
  };
}

/**
 * Update connection preview endpoint.
 */
export function updateConnecting(
  state: ViewState,
  currentPoint: Point,
): ViewState {
  if (!state.connecting) {
    return state;
  }

  return {
    ...state,
    connecting: {
      ...state.connecting,
      currentPoint,
    },
  };
}

/**
 * Finish connection (edge creation handled externally via SDK).
 */
export function finishConnecting(state: ViewState): ViewState {
  return {
    ...state,
    connecting: null,
  };
}

/**
 * Cancel connection.
 */
export function cancelConnecting(state: ViewState): ViewState {
  return {
    ...state,
    connecting: null,
  };
}

/**
 * Check if currently connecting.
 */
export function isConnecting(state: ViewState): boolean {
  return state.connecting !== null;
}

// =============================================================================
// Resize Operations
// =============================================================================

/** Default minimum node size */
export const DEFAULT_MIN_SIZE = { width: 50, height: 50 };

/**
 * Start a resize operation.
 *
 * @param state - Current view state
 * @param nodeId - Node being resized
 * @param handle - Which resize handle was grabbed
 * @param bounds - Node bounds in top-left format (x, y, width, height)
 * @param rotation - Node rotation in radians (default: 0)
 */
export function startResize(
  state: ViewState,
  nodeId: string,
  handle: ResizeHandlePosition,
  bounds: { x: number; y: number; width: number; height: number },
  rotation: number = 0,
): ViewState {
  // Convert top-left bounds to center-based bounds
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const hw = bounds.width / 2;
  const hh = bounds.height / 2;

  // Get anchor point in local coordinates (opposite corner/edge)
  const anchorLocal = getLocalAnchor(hw, hh, handle);

  return {
    ...state,
    resize: {
      nodeId,
      handle,
      rotation,
      anchorLocal,
      originalBounds: { cx, cy, width: bounds.width, height: bounds.height },
      currentBounds: { cx, cy, width: bounds.width, height: bounds.height },
    },
  };
}

/**
 * Update the resize operation as the user drags.
 *
 * Works in local (node) coordinate space for rotation-aware resizing:
 * 1. Transform mouse position to local space
 * 2. Calculate new size based on distance from anchor
 * 3. Calculate new center (midpoint of anchor and mouse in local space)
 * 4. Transform new center back to world space
 *
 * @param state - Current view state
 * @param currentPoint - Current mouse position in world coordinates
 * @param minSize - Minimum size constraints (default: 50x50)
 * @param proportional - Maintain aspect ratio (only for corner handles)
 * @param gridSize - Optional grid size for snapping (0 or undefined = no snapping)
 * @param intrinsicAspectRatio - Optional aspect ratio to use instead of current bounds (for images)
 */
export function updateResize(
  state: ViewState,
  currentPoint: Point,
  minSize: { width: number; height: number } = DEFAULT_MIN_SIZE,
  proportional: boolean = false,
  gridSize: number = 0,
  intrinsicAspectRatio?: number,
): ViewState {
  if (!state.resize) {
    return state;
  }

  const { handle, rotation, anchorLocal, originalBounds } = state.resize;

  const originalCenter = { x: originalBounds.cx, y: originalBounds.cy };

  // Transform mouse to local space (relative to original center)
  const mouseLocal = worldToLocal(currentPoint, originalCenter, rotation);

  // Calculate new size based on handle type
  let newWidth: number;
  let newHeight: number;
  let newCenterLocal: Point;

  const isCorner = ["nw", "ne", "sw", "se"].includes(handle);
  const isHorizontalEdge = ["e", "w"].includes(handle);
  const isVerticalEdge = ["n", "s"].includes(handle);

  if (isCorner) {
    // Corner handles: size = |mouseLocal - anchorLocal|
    newWidth = Math.abs(mouseLocal.x - anchorLocal.x);
    newHeight = Math.abs(mouseLocal.y - anchorLocal.y);

    // New center is midpoint of anchor and mouse in local space
    newCenterLocal = {
      x: (anchorLocal.x + mouseLocal.x) / 2,
      y: (anchorLocal.y + mouseLocal.y) / 2,
    };
  } else if (isHorizontalEdge) {
    // Horizontal edge (e/w): only width changes
    newWidth = Math.abs(mouseLocal.x - anchorLocal.x);
    newHeight = originalBounds.height;

    // Center moves only in x
    newCenterLocal = {
      x: (anchorLocal.x + mouseLocal.x) / 2,
      y: 0, // Center stays at local y=0 (centered vertically)
    };
  } else {
    // Vertical edge (n/s): only height changes
    newWidth = originalBounds.width;
    newHeight = Math.abs(mouseLocal.y - anchorLocal.y);

    // Center moves only in y
    newCenterLocal = {
      x: 0, // Center stays at local x=0 (centered horizontally)
      y: (anchorLocal.y + mouseLocal.y) / 2,
    };
  }

  // Apply minimum size constraints
  if (newWidth < minSize.width) {
    newWidth = minSize.width;
    // Recalculate center x to keep anchor fixed
    if (isCorner || isHorizontalEdge) {
      const signX = mouseLocal.x > anchorLocal.x ? 1 : -1;
      newCenterLocal = {
        ...newCenterLocal,
        x: anchorLocal.x + (signX * newWidth) / 2,
      };
    }
  }
  if (newHeight < minSize.height) {
    newHeight = minSize.height;
    // Recalculate center y to keep anchor fixed
    if (isCorner || isVerticalEdge) {
      const signY = mouseLocal.y > anchorLocal.y ? 1 : -1;
      newCenterLocal = {
        ...newCenterLocal,
        y: anchorLocal.y + (signY * newHeight) / 2,
      };
    }
  }

  // Apply proportional resize
  if (proportional) {
    // Use intrinsic aspect ratio if provided (e.g., for images), otherwise use current bounds
    const originalAspect =
      intrinsicAspectRatio ?? originalBounds.width / originalBounds.height;
    const currentAspect = newWidth / newHeight;

    if (isCorner) {
      // Corner handles: constrain to aspect ratio
      if (currentAspect > originalAspect) {
        // Width is proportionally larger, adjust it
        const targetWidth = newHeight * originalAspect;
        const signX = mouseLocal.x > anchorLocal.x ? 1 : -1;
        newWidth = targetWidth;
        newCenterLocal = {
          ...newCenterLocal,
          x: anchorLocal.x + (signX * targetWidth) / 2,
        };
      } else {
        // Height is proportionally larger, adjust it
        const targetHeight = newWidth / originalAspect;
        const signY = mouseLocal.y > anchorLocal.y ? 1 : -1;
        newHeight = targetHeight;
        newCenterLocal = {
          ...newCenterLocal,
          y: anchorLocal.y + (signY * targetHeight) / 2,
        };
      }
    } else if (isHorizontalEdge) {
      // Horizontal edge: width drives, height adjusts centered
      newHeight = newWidth / originalAspect;
    } else if (isVerticalEdge) {
      // Vertical edge: height drives, width adjusts centered
      newWidth = newHeight * originalAspect;
    }

    // Re-apply minimum size after proportional adjustment
    if (newWidth < minSize.width) {
      const scale = minSize.width / newWidth;
      newWidth = minSize.width;
      newHeight = newHeight * scale;
    }
    if (newHeight < minSize.height) {
      const scale = minSize.height / newHeight;
      newHeight = minSize.height;
      newWidth = newWidth * scale;
    }
  }

  // Transform new center back to world space
  const newCenter = localToWorld(newCenterLocal, originalCenter, rotation);

  // Apply grid snapping if enabled
  let finalCx = newCenter.x;
  let finalCy = newCenter.y;
  let finalWidth = newWidth;
  let finalHeight = newHeight;

  if (gridSize > 0) {
    // Calculate top-left position from center
    const topLeftX = finalCx - finalWidth / 2;
    const topLeftY = finalCy - finalHeight / 2;

    // Snap top-left to grid
    const snappedTopLeftX = Math.round(topLeftX / gridSize) * gridSize;
    const snappedTopLeftY = Math.round(topLeftY / gridSize) * gridSize;

    // Snap size to grid (round to nearest grid unit)
    finalWidth = Math.round(finalWidth / gridSize) * gridSize;
    finalHeight = Math.round(finalHeight / gridSize) * gridSize;

    // Ensure minimum size after snapping
    if (finalWidth < minSize.width)
      finalWidth = Math.ceil(minSize.width / gridSize) * gridSize;
    if (finalHeight < minSize.height)
      finalHeight = Math.ceil(minSize.height / gridSize) * gridSize;

    // Recalculate center from snapped top-left and snapped size
    finalCx = snappedTopLeftX + finalWidth / 2;
    finalCy = snappedTopLeftY + finalHeight / 2;
  }

  return {
    ...state,
    resize: {
      ...state.resize,
      currentBounds: {
        cx: finalCx,
        cy: finalCy,
        width: finalWidth,
        height: finalHeight,
      },
    },
  };
}

/**
 * Finish resize operation and return final bounds.
 */
export function finishResize(state: ViewState): ViewState {
  return {
    ...state,
    resize: null,
  };
}

/**
 * Cancel resize operation (positions not committed).
 */
export function cancelResize(state: ViewState): ViewState {
  return {
    ...state,
    resize: null,
  };
}

/**
 * Check if currently resizing.
 */
export function isResizing(state: ViewState): boolean {
  return state.resize !== null;
}

/**
 * Get the current resize bounds (if resizing).
 * Returns bounds in top-left format for compatibility with external APIs.
 */
export function getResizeBounds(
  state: ViewState,
): { x: number; y: number; width: number; height: number } | null {
  if (!state.resize) {
    return null;
  }
  const { cx, cy, width, height } = state.resize.currentBounds;
  return {
    x: cx - width / 2,
    y: cy - height / 2,
    width,
    height,
  };
}

/**
 * Set hover state for a resize handle.
 */
export function setHoveredResizeHandle(
  state: ViewState,
  handleId: string | null,
): ViewState {
  if (state.hovered.resizeHandleId === handleId) {
    return state;
  }

  return {
    ...state,
    hovered: {
      ...state.hovered,
      resizeHandleId: handleId,
    },
  };
}

// =============================================================================
// Rotation Operations
// =============================================================================

/**
 * Set hover state for rotation zone.
 */
export function setHoveredRotationZone(
  state: ViewState,
  inZone: boolean,
): ViewState {
  if (state.hovered.rotationZone === inZone) {
    return state;
  }

  return {
    ...state,
    hovered: {
      ...state.hovered,
      rotationZone: inZone,
    },
  };
}

/**
 * Start a rotation operation.
 */
export function startRotation(
  state: ViewState,
  nodeId: string,
  centerPoint: Point,
  startAngle: number,
  originalRotation: number,
): ViewState {
  return {
    ...state,
    rotate: {
      nodeId,
      centerPoint,
      startAngle,
      currentAngle: startAngle,
      originalRotation,
    },
  };
}

/**
 * Update the rotation operation as the user drags.
 */
export function updateRotation(
  state: ViewState,
  currentAngle: number,
): ViewState {
  if (!state.rotate) {
    return state;
  }

  return {
    ...state,
    rotate: {
      ...state.rotate,
      currentAngle,
    },
  };
}

/**
 * Finish rotation operation.
 */
export function finishRotation(state: ViewState): ViewState {
  return {
    ...state,
    rotate: null,
  };
}

/**
 * Cancel rotation operation (rotation not committed).
 */
export function cancelRotation(state: ViewState): ViewState {
  return {
    ...state,
    rotate: null,
  };
}

/**
 * Check if currently rotating.
 */
export function isRotating(state: ViewState): boolean {
  return state.rotate !== null;
}

/**
 * Get the rotation delta (current - start) in radians.
 */
export function getRotationDelta(state: ViewState): number | null {
  if (!state.rotate) {
    return null;
  }
  return state.rotate.currentAngle - state.rotate.startAngle;
}

/**
 * Get the final rotation value (original + delta).
 */
export function getFinalRotation(state: ViewState): number | null {
  if (!state.rotate) {
    return null;
  }
  const delta = state.rotate.currentAngle - state.rotate.startAngle;
  return state.rotate.originalRotation + delta;
}

// =============================================================================
// Drop Target Operations
// =============================================================================

/**
 * Show the drop target indicator (files being dragged over canvas).
 */
export function showDropTarget(state: ViewState): ViewState {
  if (state.dropTarget) return state;
  return { ...state, dropTarget: true };
}

/**
 * Hide the drop target indicator.
 */
export function hideDropTarget(state: ViewState): ViewState {
  if (!state.dropTarget) return state;
  return { ...state, dropTarget: false };
}

// =============================================================================
// Geometry Helpers
// =============================================================================

/**
 * Check if a point is inside a rectangle.
 */
export function pointInRect(point: Point, rect: SelectionRect): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

/**
 * Check if two rectangles intersect.
 */
export function rectsIntersect(a: SelectionRect, b: SelectionRect): boolean {
  return !(
    a.x + a.width < b.x ||
    b.x + b.width < a.x ||
    a.y + a.height < b.y ||
    b.y + b.height < a.y
  );
}

/**
 * Get nodes that intersect with a selection rectangle.
 */
export function getNodesInRect(
  nodes: EditorNode[],
  rect: SelectionRect,
): string[] {
  return nodes
    .filter((node) => {
      const nodeRect: SelectionRect = {
        x: node.position.x,
        y: node.position.y,
        width: node.size.width,
        height: node.size.height,
      };
      return rectsIntersect(rect, nodeRect);
    })
    .map((node) => node.id);
}

// =============================================================================
// Edge Geometry Helpers
// =============================================================================

/**
 * Get the center point of a node.
 */
function getNodeCenter(node: EditorNode): Point {
  return {
    x: node.position.x + node.size.width / 2,
    y: node.position.y + node.size.height / 2,
  };
}

/**
 * Check if a line segment intersects with a rectangle.
 */
function lineIntersectsRect(
  start: Point,
  end: Point,
  rect: SelectionRect,
): boolean {
  const rectRight = rect.x + rect.width;
  const rectBottom = rect.y + rect.height;

  // Check if either endpoint is inside the rect
  const startInside =
    start.x >= rect.x &&
    start.x <= rectRight &&
    start.y >= rect.y &&
    start.y <= rectBottom;
  const endInside =
    end.x >= rect.x &&
    end.x <= rectRight &&
    end.y >= rect.y &&
    end.y <= rectBottom;
  if (startInside || endInside) return true;

  // Check line-rect intersection using parametric line equation
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  const tValues: number[] = [];

  if (dx !== 0) {
    tValues.push((rect.x - start.x) / dx); // left edge
    tValues.push((rectRight - start.x) / dx); // right edge
  }
  if (dy !== 0) {
    tValues.push((rect.y - start.y) / dy); // top edge
    tValues.push((rectBottom - start.y) / dy); // bottom edge
  }

  for (const t of tValues) {
    if (t >= 0 && t <= 1) {
      const x = start.x + t * dx;
      const y = start.y + t * dy;
      if (x >= rect.x && x <= rectRight && y >= rect.y && y <= rectBottom) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Get edges that intersect with a selection rectangle.
 * Uses node centers as edge endpoints.
 */
export function getEdgesInRect(
  edges: EditorEdge[],
  nodes: EditorNode[],
  rect: SelectionRect,
): string[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  return edges
    .filter((edge) => {
      const sourceNode = nodeMap.get(edge.sourceNodeId);
      const targetNode = nodeMap.get(edge.targetNodeId);
      if (!sourceNode || !targetNode) return false;

      const start = getNodeCenter(sourceNode);
      const end = getNodeCenter(targetNode);

      return lineIntersectsRect(start, end, rect);
    })
    .map((edge) => edge.id);
}
